import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Sparkles, Wrench, ClipboardList, Check, X, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import InlinePhotoAttachments from './InlinePhotoAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter-176 · Phase 3 — Redesigned Request a Property Service form.
 *
 * Supports multi-select service picking, smart calendar (past dates
 * disabled), full-width Additional Instructions field, and a
 * bottom summary panel. Submit stays disabled until at least one
 * service AND one date are selected. Each submitted service becomes
 * its own row in the request history, tagged with the selected dates
 * and the shared instructions block.
 *
 * Full specialty catalog (Iter-176 Phase 2) — Deep Cleaning included.
 */
const PM_SERVICE_TYPES = [
  { key: 'bulk_removal', label: 'Bulk Removal', hint: 'Pickup of bulk items staged near the dumpster or trash area.' },
  { key: 'pressure_washing', label: 'Pressure Washing', hint: 'Exterior pressure washing of designated common areas.' },
  { key: 'window_cleaning', label: 'Window Cleaning', hint: 'Interior & exterior window cleaning per your tier cadence.' },
  { key: 'dumpster_treatment', label: 'Dumpster Treatment', hint: 'Bi-annual / quarterly deodorize & sanitize per your tier.' },
  { key: 'trash_room_maintenance', label: 'Trash Room Maintenance', hint: 'Deep clean of interior trash rooms + odor control between service days.' },
  { key: 'eco_friendly_amenities', label: 'Eco-friendly Amenities Cleaning', hint: 'Sustainable amenity cleaning (Presidential tier).' },
  { key: 'deep_cleaning', label: 'Deep Cleaning', hint: 'Full-scope deep cleaning for common areas, amenities, or move-out units.' },
  { key: 'carpet_cleaning_extraction', label: 'Carpet Cleaning & Extraction', hint: 'Deep carpet cleaning + hot-water extraction for common areas, offices, and lobbies.' },
  { key: 'floor_care', label: 'Floor Care', hint: 'Routine floor cleaning programs matched to every surface type.' },
  { key: 'floor_polishing', label: 'Floor Polishing', hint: 'Professional polishing that restores shine and protects surfaces.' },
  { key: 'floor_restoration', label: 'Floor Restoration', hint: 'Deep restoration that brings worn floors back to standard.' },
  { key: 'day_porter', label: 'Day Porter', hint: 'On-site NECS porter during business hours — spot-clean, restock, quick-response.' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

function calendarGrid(anchor) {
  // anchor is a Date pointing at the first day of the visible month.
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PropertyServicesRequest({ user }) {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [priority, setPriority] = useState('normal');
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [calAnchor, setCalAnchor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const fetchRequests = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/service-requests`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.filter((r) =>
          PM_SERVICE_TYPES.some((t) => t.key === r.request_type),
        ));
      }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const toggleType = (key) => {
    setSelectedTypes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const removeType = (key) => {
    setSelectedTypes((prev) => prev.filter((k) => k !== key));
  };

  const toggleDate = (iso) => {
    setSelectedDates((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso].sort());
  };

  const canSubmit = selectedTypes.length > 0 && selectedDates.length > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const dateSuffix = ` [Requested date(s): ${selectedDates.join(', ')}]`;
      const results = await Promise.all(selectedTypes.map(async (key) => {
        const label = PM_SERVICE_TYPES.find((t) => t.key === key)?.label || key;
        const description = (instructions.trim() || `Property service — ${label}`) + dateSuffix;
        const res = await fetch(`${BACKEND_URL}/api/service-requests`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            request_type: key,
            description,
            priority,
            requested_dates: selectedDates,
          }),
        });
        return res.ok;
      }));
      const allOk = results.every(Boolean);
      if (allOk) {
        toast.success(`Submitted ${selectedTypes.length} service request${selectedTypes.length > 1 ? 's' : ''}.`);
        setSelectedTypes([]);
        setSelectedDates([]);
        setInstructions('');
        setPriority('normal');
        fetchRequests();
      } else {
        toast.error('Some requests failed. Please retry.');
      }
    } catch (_) {
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  };

  const cells = useMemo(() => calendarGrid(calAnchor), [calAnchor]);
  const today = todayISO();
  const monthLabel = calAnchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4" data-testid="property-services-request">
      <Card className="p-6 border-2" style={{ borderColor: '#C9A962' }}>
        <h3 className="font-bold text-lg flex items-center gap-2 mb-3"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Sparkles className="h-5 w-5" style={{ color: '#C9A962' }} />
          REQUEST A PROPERTY SERVICE
        </h3>
        <p className="text-xs text-zinc-600 mb-4 leading-relaxed">
          Select every service you need for {user?.property_name || 'your property'}, choose preferred date(s),
          and add any special instructions. NECS will confirm scheduling and log everything in your Daily Reports.
        </p>

        <form onSubmit={submit} className="space-y-5">
          {/* Service picker (multi-select) */}
          <div>
            <Label>Services (choose one or more)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1"
                 data-testid="pm-service-type-grid">
              {PM_SERVICE_TYPES.map((t) => {
                const selected = selectedTypes.includes(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => toggleType(t.key)}
                    aria-pressed={selected}
                    className={`relative p-3 pl-9 rounded border-2 text-left transition ${
                      selected
                        ? 'border-[#C9A962] bg-white ring-2 ring-[#C9A962]/40 shadow-sm'
                        : 'border-zinc-200 bg-white hover:border-[#C9A962]/60'
                    }`}
                    data-testid={`pm-service-type-${t.key}`}
                    data-checked={selected}
                  >
                    <span
                      className={`absolute left-2.5 top-3 flex items-center justify-center w-5 h-5 rounded border-2 transition ${
                        selected ? 'bg-[#C9A962] border-[#C9A962]' : 'bg-white border-zinc-400'
                      }`}
                      aria-hidden="true"
                    >
                      {selected && <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />}
                    </span>
                    <p className={`text-sm font-semibold ${selected ? 'text-[#2D2D2D]' : 'text-zinc-800'}`}>
                      {t.label}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{t.hint}</p>
                  </button>
                );
              })}
            </div>

            {/* Chip summary — removable */}
            {selectedTypes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5"
                   data-testid="pm-selected-services-summary">
                {selectedTypes.map((key) => {
                  const label = PM_SERVICE_TYPES.find((t) => t.key === key)?.label || key;
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-[#C9A962] text-black"
                      data-testid={`pm-selected-chip-${key}`}
                    >
                      {label}
                      <button
                        type="button"
                        aria-label={`Remove ${label}`}
                        onClick={() => removeType(key)}
                        className="ml-1 rounded-full hover:bg-black/10"
                        data-testid={`pm-selected-chip-remove-${key}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Smart calendar — Iter-180 · Phase 4 opens as a popover
              triggered by the calendar icon. Grid stays anchored to
              the icon and closes on date selection. Reopening jumps
              back to the previously-selected month. */}
          <div className="relative" data-testid="pm-service-calendar">
            <Label className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCalOpen((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 border border-zinc-300 bg-white hover:border-[#C9A962] transition"
                aria-expanded={calOpen}
                data-testid="pm-cal-toggle"
              >
                <CalendarDays className="h-4 w-4 text-[#C9A962]" />
                <span className="text-xs font-bold text-zinc-800">
                  Preferred date(s)
                </span>
              </button>
              {selectedDates.length > 0 && (
                <span className="text-[11px] text-zinc-600">
                  {selectedDates.length} selected
                </span>
              )}
            </Label>
            {calOpen && (
              <div className="absolute z-40 mt-2 w-80 rounded-lg border border-zinc-300 bg-white p-3 shadow-xl"
                   data-testid="pm-cal-popup">
                <div className="flex items-center justify-between mb-2">
                  <button type="button"
                          onClick={() => setCalAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                          className="px-2 py-1 text-sm font-bold rounded hover:bg-zinc-100"
                          data-testid="pm-cal-prev">‹</button>
                  <span className="text-sm font-bold text-zinc-900"
                        data-testid="pm-cal-month">{monthLabel}</span>
                  <button type="button"
                          onClick={() => setCalAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                          className="px-2 py-1 text-sm font-bold rounded hover:bg-zinc-100"
                          data-testid="pm-cal-next">›</button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-zinc-500 mb-1">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
                    <div key={d} className="uppercase tracking-widest">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((cell, idx) => {
                    if (!cell) return <div key={idx} />;
                    const iso = cell.toISOString().slice(0, 10);
                    const past = iso < today;
                    const active = selectedDates.includes(iso);
                    return (
                      <button
                        key={iso}
                        type="button"
                        disabled={past}
                        onClick={() => {
                          toggleDate(iso);
                          setCalOpen(false);
                        }}
                        className={`aspect-square text-xs rounded-md transition ${
                          past
                            ? 'text-zinc-300 cursor-not-allowed'
                            : active
                            ? 'bg-[#C9A962] text-black font-bold'
                            : 'bg-white text-zinc-800 hover:bg-[#C9A962]/20'
                        }`}
                        data-testid={`pm-cal-day-${iso}`}
                        data-active={active || undefined}
                        data-past={past || undefined}
                      >
                        {cell.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {selectedDates.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5"
                   data-testid="pm-selected-dates-summary">
                {selectedDates.map((iso) => (
                  <span key={iso}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-black text-white"
                        data-testid={`pm-selected-date-chip-${iso}`}>
                    {fmtDate(iso)}
                    <button type="button"
                            aria-label={`Remove ${iso}`}
                            onClick={() => toggleDate(iso)}
                            className="ml-1 rounded-full hover:bg-white/20"
                            data-testid={`pm-selected-date-remove-${iso}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Additional Instructions */}
          <div>
            <Label htmlFor="pm-service-instructions">Additional Instructions</Label>
            <Textarea
              id="pm-service-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={4}
              placeholder="Access instructions, preferences, staging, pet notes, contact person — anything the crew should know."
              className="focus-visible:ring-[#C9A962] w-full"
              data-testid="pm-service-instructions"
            />
          </div>

          {/* Priority */}
          <div className="flex items-center gap-2">
            <Label className="whitespace-nowrap text-xs mb-0">Priority:</Label>
            {['normal', 'urgent'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                className={`px-3 py-1 rounded text-xs font-semibold transition ${
                  priority === p
                    ? 'bg-[#C9A962] text-black'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
                data-testid={`pm-service-priority-${p}`}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Bottom summary panel */}
          <div className="rounded-lg border-2 border-black bg-zinc-50 p-4"
               data-testid="pm-service-summary-panel">
            <h4 className="text-sm font-bold text-black uppercase tracking-widest mb-2">
              Summary
            </h4>
            <dl className="text-sm text-black space-y-1">
              <div className="flex flex-wrap gap-1">
                <dt className="font-bold w-32">Services:</dt>
                <dd className="flex-1 min-w-0" data-testid="pm-summary-services">
                  {selectedTypes.length === 0
                    ? <em className="text-zinc-500">None selected yet</em>
                    : selectedTypes.map((k) => PM_SERVICE_TYPES.find((t) => t.key === k)?.label || k).join(', ')}
                </dd>
              </div>
              <div className="flex flex-wrap gap-1">
                <dt className="font-bold w-32">Scheduled date(s):</dt>
                <dd className="flex-1 min-w-0" data-testid="pm-summary-dates">
                  {selectedDates.length === 0
                    ? <em className="text-zinc-500">Pick at least one date</em>
                    : selectedDates.map(fmtDate).join(', ')}
                </dd>
              </div>
              <div className="flex flex-wrap gap-1">
                <dt className="font-bold w-32">Instructions:</dt>
                <dd className="flex-1 min-w-0 whitespace-pre-wrap"
                    data-testid="pm-summary-instructions">
                  {instructions.trim() || <em className="text-zinc-500">None</em>}
                </dd>
              </div>
            </dl>
            <div className="mt-3 flex items-center justify-end">
              <Button
                type="submit"
                disabled={!canSubmit}
                className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pm-service-submit"
              >
                {busy ? 'Submitting…' : `Submit ${selectedTypes.length > 1 ? `${selectedTypes.length} requests` : 'request'}`}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      <Card className="p-5" data-testid="pm-service-history">
        <h3 className="font-bold text-base flex items-center gap-2 mb-3"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <ClipboardList className="h-5 w-5 text-zinc-500" />
          REQUEST HISTORY · {requests.length}
        </h3>
        {requests.length === 0 ? (
          <p className="text-xs text-zinc-500">No property services requested yet.</p>
        ) : (
          <ul className="space-y-2">
            {requests.map((r) => {
              const label = PM_SERVICE_TYPES.find((t) => t.key === r.request_type)?.label
                || r.request_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
              const expanded = expandedRow === r.request_id;
              return (
                <li key={r.request_id}
                    className="rounded border border-zinc-200"
                    data-testid={`pm-service-row-${r.request_id}`}>
                  <div className="flex items-center justify-between p-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold flex items-center gap-1.5">
                        <Wrench className="h-3.5 w-3.5 text-zinc-400" />
                        {label}
                      </p>
                      <p className="text-[11px] text-zinc-500 truncate max-w-md">{r.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] font-bold ${
                        r.status === 'completed' ? 'bg-green-500' :
                        r.status === 'in_progress' ? 'bg-blue-500' :
                        r.status === 'scheduled' ? 'bg-amber-500' : 'bg-zinc-400'
                      } text-white`}>
                        {(r.status || 'pending').toUpperCase()}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => setExpandedRow(expanded ? null : r.request_id)}
                        className="text-[11px] font-bold underline text-[#C9A962] hover:text-[#B8975A]"
                        data-testid={`pm-service-row-toggle-${r.request_id}`}
                      >
                        {expanded ? 'Hide photos' : 'Photos'}
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t border-zinc-200 p-3 bg-zinc-50">
                      <InlinePhotoAttachments
                        parentKind="service_request"
                        parentId={r.request_id}
                        testIdPrefix={`sr-${r.request_id}`}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
